/* Owner ERP — Divisions CRUD, Members & Roles, Swish pool sections */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ListChecks, Star, Trash2, Send, X, Pencil, PauseCircle, PlayCircle, RefreshCw, SlidersHorizontal, BarChart3, Users, UserPlus, Wallet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from '../../utils/api';
import { GuestDetailsSwitch } from '../../components/GuestDetails';
import { fmtKr, fmtDate, Panel, SectionTitle, StatusBadge, RoleBadge, Meter, UpgradeDialog, atCap } from './ownerUi';

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return 'Error';
};

/* ============================== Divisions ============================== */

export function DivisionsSection({ orgId, headers, t, setNav, setReportDivision }) {
  const [subs, setSubs] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', data}
  const [swishAccounts, setSwishAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [upgrade, setUpgrade] = useState(false);
  const [teamDiv, setTeamDiv] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, sw] = await Promise.all([
        axios.get(`${API}/business/sub-accounts`, { headers, params: { status: 'ACTIVE' } }),
        axios.get(`${API}/business/swish-accounts`, { headers }),
      ]);
      setSubs(s.data.items || []);
      setSwishAccounts(sw.data.items || []);
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [headers, orgId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/org/${orgId}/usage`, { headers }).then((u) => setUsage(u.data)).catch(() => {});
  }, [orgId, headers]);

  const emptyForm = (type) => ({ name: '', description: '', type, parent_sub_account_id: '', swish_account_id: '', swish_choice_mode: 'locked', require_guest_details: true });
  const openCreate = () => {
    if (atCap(usage?.divisions)) { setUpgrade(true); return; }
    setDialog({ mode: 'create', data: emptyForm('DIVISION') });
  };
  const openEdit = (s) => setDialog({ mode: 'edit', data: { ...s, parent_sub_account_id: s.parent_sub_account_id || '', swish_account_id: s.swish_account_id || '', swish_choice_mode: s.swish_choice_mode || 'locked', require_guest_details: s.require_guest_details !== false } });

  const save = async () => {
    const d = dialog.data;
    if (!d.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: d.name, description: d.description || null, type: d.type,
        parent_sub_account_id: d.parent_sub_account_id || (dialog.mode === 'edit' ? '' : null),
        swish_account_id: d.swish_account_id || null,
        swish_choice_mode: d.swish_choice_mode,
        require_guest_details: d.require_guest_details !== false,
      };
      if (dialog.mode === 'create') {
        await axios.post(`${API}/business/sub-accounts`, payload, { headers });
      } else {
        await axios.put(`${API}/business/sub-accounts/${d.id}`, payload, { headers });
      }
      toast.success(t.saved);
      setDialog(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(t.confirm_delete)) return;
    try {
      await axios.delete(`${API}/business/sub-accounts/${s.id}`, { headers });
      toast.success(t.removed); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const parentName = (id) => subs?.find((x) => x.id === id)?.name;

  return (
    <div className="space-y-4">
      <Panel testId="owner-divisions-table">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <SectionTitle sub={t.divisions_sub}>{t.divisions_title}</SectionTitle>
          <Button size="sm" data-testid="owner-new-division" className="bg-teal-700 hover:bg-teal-800 text-white h-8 text-[13px]" onClick={openCreate}>
            + {t.new_sub}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] uppercase tracking-wide text-slate-400 border-y border-slate-100 bg-slate-50/60">
                <th className="px-5 py-2.5 font-semibold">{t.name}</th>
                <th className="px-3 py-2.5 font-semibold">{t.type}</th>
                <th className="px-3 py-2.5 font-semibold">{t.th_mods}</th>
                <th className="px-3 py-2.5 font-semibold">{t.payout_swish}</th>
                <th className="px-3 py-2.5 font-semibold text-right">{t.collected}</th>
                <th className="px-3 py-2.5 font-semibold">{t.status}</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {(subs || []).map((s) => (
                <tr key={s.id} data-testid={`owner-division-row-${s.id}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-teal-50/40 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-800">{s.name}</div>
                    <div className="text-[11.5px] text-slate-400">
                      {s.display_paymig_id}{s.parent_sub_account_id ? ` · ↳ ${parentName(s.parent_sub_account_id) || ''}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <Badge className={`${s.type === 'DIVISION' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-100 text-slate-600 border-slate-200'} border shadow-none font-medium hover:bg-inherit`}>
                      {s.type === 'DIVISION' ? t.division : t.event}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {(s.moderators || []).length ? (s.moderators || []).map((m) => m.user_name).filter(Boolean).join(', ') || (s.moderators || []).length : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      {s.swish_choice_mode === 'admin_choice'
                        ? <ListChecks size={13} className="text-sky-500" title={t.mode_admin_choice} />
                        : <Lock size={13} className="text-slate-400" title={t.mode_locked} />}
                      {s.connected_swish_id || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-teal-700">{fmtKr(s.total_received)}</td>
                  <td className="px-3 py-3"><StatusBadge value={s.status} t={t} /></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 justify-end">
                      <button className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50" title={t.view_reports}
                              data-testid={`owner-division-reports-${s.id}`} onClick={() => { setReportDivision(s.id); setNav('reports'); }}><BarChart3 size={14} /></button>
                      <button className="p-1.5 rounded-md text-slate-400 hover:text-sky-700 hover:bg-sky-50" title={t.manage_team}
                              data-testid={`owner-division-team-${s.id}`} onClick={() => setTeamDiv(s)}><Users size={14} /></button>
                      <button className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50" title={t.edit}
                              data-testid={`owner-edit-sub-${s.id}`} onClick={() => openEdit(s)}><Pencil size={14} /></button>
                      <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50" title={t.delete}
                              onClick={() => remove(s)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!subs && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">{t.loading}</td></tr>
              )}
              {subs && subs.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">{t.no_data}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <UpgradeDialog open={upgrade} onOpenChange={setUpgrade} t={t} body={t.cap_divisions}
        onUpgrade={() => { setUpgrade(false); setNav('billing'); }}
        secondaryLabel={t.create_event_instead}
        onSecondary={() => { setUpgrade(false); setDialog({ mode: 'create', data: emptyForm('EVENT') }); }} />

      {teamDiv && (
        <ManageTeamDialog orgId={orgId} headers={headers} t={t} division={teamDiv} onClose={() => setTeamDiv(null)} />
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="owner-division-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'create' ? t.new_sub : t.edit}</DialogTitle>
            <DialogDescription className="sr-only">{dialog?.mode === 'create' ? t.new_sub : t.edit}</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div><Label className="text-[12px]">{t.name}</Label>
                <Input data-testid="owner-sub-name" value={dialog.data.name} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))} /></div>
              <div><Label className="text-[12px]">{t.description}</Label>
                <Input value={dialog.data.description || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, description: e.target.value } }))} /></div>
              {dialog.mode === 'create' && (
                <div><Label className="text-[12px]">{t.type}</Label>
                  <Select value={dialog.data.type} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, type: v } }))}>
                    <SelectTrigger data-testid="owner-sub-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DIVISION">{t.division}</SelectItem>
                      <SelectItem value="EVENT">{t.event}</SelectItem>
                    </SelectContent>
                  </Select></div>
              )}
              <div><Label className="text-[12px]">{t.parent}</Label>
                <Select value={dialog.data.parent_sub_account_id || 'none'}
                        onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, parent_sub_account_id: v === 'none' ? '' : v } }))}>
                  <SelectTrigger data-testid="owner-sub-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.none}</SelectItem>
                    {(subs || []).filter((x) => x.id !== dialog.data.id).map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>))}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-[12px]">{t.payout_swish}</Label>
                <Select value={dialog.data.swish_account_id || 'auto'}
                        onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, swish_account_id: v === 'auto' ? '' : v } }))}>
                  <SelectTrigger data-testid="owner-sub-swish"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t.primary}</SelectItem>
                    {swishAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.display_name} · {a.merchant_number}</SelectItem>))}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-[12px]">{t.payout_mode}</Label>
                <Select value={dialog.data.swish_choice_mode}
                        onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, swish_choice_mode: v } }))}>
                  <SelectTrigger data-testid="owner-sub-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locked">{t.mode_locked}</SelectItem>
                    <SelectItem value="admin_choice">{t.mode_admin_choice}</SelectItem>
                  </SelectContent>
                </Select></div>
              <GuestDetailsSwitch checked={dialog.data.require_guest_details}
                label={t.require_guest_details} hint={t.require_guest_details_hint} testId="owner-sub-require-guest-details"
                onChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, require_guest_details: v } }))} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>{t.cancel}</Button>
            <Button data-testid="owner-sub-save" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={save}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== Members ============================== */

export function MembersSection({ orgId, headers, t, lang, setNav }) {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [manageDlg, setManageDlg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [savingManage, setSavingManage] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkRole, setBulkRole] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [swishUsed, setSwishUsed] = useState(null);
  const [form, setForm] = useState({ role: 'moderator', scope_id: '', email: '', phone: '', name: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      // Members first (fast) — sub-accounts (slow aggregation) only feeds the invite dialog
      const m = await axios.get(`${API}/org/${orgId}/members`, { headers });
      setData(m.data);
      axios.get(`${API}/business/sub-accounts`, { headers, params: { status: 'ACTIVE' } })
        .then((s) => setSubs(s.data.items || [])).catch(() => {});
      axios.get(`${API}/org/${orgId}/usage`, { headers })
        .then((u) => setSwishUsed(u.data?.swish_accounts?.used ?? 0)).catch(() => {});
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [orgId, headers]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.email && !form.phone) { toast.error(t.email_or_phone_required); return; }
    setSaving(true);
    try {
      const payload = { role: form.role, email: form.email || null, phone: form.phone || null, name: form.name || null };
      if (form.role !== 'revisor') payload.scope_id = form.scope_id;
      await axios.post(`${API}/org/${orgId}/members`, payload, { headers });
      toast.success(t.invited);
      setDialog(false);
      setForm({ role: 'moderator', scope_id: '', email: '', phone: '', name: '' });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const cancelInvite = async (m) => {
    try { await axios.post(`${API}/org/${orgId}/members/${m.id}/cancel`, {}, { headers }); toast.success(t.cancelled); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(t.confirm_remove)) return;
    try { await axios.delete(`${API}/org/${orgId}/members/${m.id}`, { headers }); toast.success(t.removed); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const suspend = async (m) => {
    if (!window.confirm(t.confirm_suspend)) return;
    setBusyId(m.id);
    setData((d) => d && ({ ...d, items: d.items.map((x) => x.id === m.id ? { ...x, status: 'suspended' } : x),
      seats: d.seats ? { ...d.seats, used: Math.max(0, (d.seats.used || 0) - 1) } : d.seats }));
    try { await axios.post(`${API}/org/${orgId}/members/${m.id}/suspend`, {}, { headers }); toast.success(t.member_suspended); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); load(); }
  };
  const reactivate = async (m) => {
    setBusyId(m.id);
    setData((d) => d && ({ ...d, items: d.items.map((x) => x.id === m.id ? { ...x, status: 'accepted' } : x),
      seats: d.seats ? { ...d.seats, used: (d.seats.used || 0) + 1 } : d.seats }));
    try { await axios.post(`${API}/org/${orgId}/members/${m.id}/reactivate`, {}, { headers }); toast.success(t.member_reactivated); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); load(); }
  };
  const resend = async (m) => {
    setBusyId(m.id);
    try { await axios.post(`${API}/org/${orgId}/members/${m.id}/resend`, {}, { headers }); toast.success(t.invite_resent); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); load(); }
  };
  const openManage = (m) => setManageDlg({ id: m.id, role: m.role, scope_id: m.scope_id || '', scope_name: m.scope_name });
  const saveManage = async () => {
    setSavingManage(true);
    try {
      const payload = { role: manageDlg.role };
      if (manageDlg.role !== 'revisor') payload.scope_id = manageDlg.scope_id || null;
      await axios.put(`${API}/org/${orgId}/members/${manageDlg.id}`, payload, { headers });
      toast.success(t.saved); setManageDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingManage(false); }
  };

  const seats = data?.seats || {};
  const deliveryLabel = { in_app: t.delivery_in_app, email: t.delivery_email, sms: t.delivery_sms, failed: t.delivery_failed };

  const items = data?.items || [];
  const allSel = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSel ? [] : items.map((m) => m.id));
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const runBulk = async (action, extra = {}) => {
    if (!selected.length) return;
    if (action === 'remove' && !window.confirm(t.confirm_bulk_remove)) return;
    if (action === 'suspend' && !window.confirm(t.confirm_bulk_suspend)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/org/${orgId}/members/bulk`, { action, member_ids: selected, ...extra }, { headers });
      toast.success(t.bulk_done.replace('{ok}', res.data.ok_count).replace('{failed}', res.data.failed_count));
      setSelected([]); setBulkRole(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Panel className="p-5" testId="owner-seats-meter">
        <div className="flex items-center justify-between">
          <SectionTitle>{t.seats_plan}</SectionTitle>
          <span className="text-[13px] font-semibold tabular-nums">{seats.used ?? 0} / {seats.limit ?? '∞'} {t.seats_used}</span>
        </div>
        <div className="mt-3"><Meter used={seats.used ?? 0} limit={seats.limit} /></div>
      </Panel>

      {swishUsed === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="owner-swish-reminder">
          <Wallet size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-amber-900">{t.swish_reminder_title}</div>
            <div className="text-[12px] text-amber-700 mt-0.5">{t.swish_reminder_desc}</div>
          </div>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-[12px] shrink-0"
                  onClick={() => setNav('swish')} data-testid="owner-swish-reminder-cta">{t.add_swish_account}</Button>
        </div>
      )}

      <Panel testId="owner-members-table">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <SectionTitle>{t.members_title}</SectionTitle>
          <Button size="sm" data-testid="owner-invite-member" className="bg-teal-700 hover:bg-teal-800 text-white h-8 text-[13px]"
                  onClick={() => (atCap(seats) ? setUpgrade(true) : setDialog(true))}>
            + {t.invite_member}
          </Button>
        </div>
        {selected.length > 0 && (
          <div className="mx-5 mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-teal-50 border border-teal-100 px-3 py-2" data-testid="owner-bulk-bar">
            <span className="text-[12.5px] font-semibold text-teal-800" data-testid="owner-bulk-count">{selected.length} {t.selected_count}</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="h-7 text-[12px]" disabled={bulkBusy} data-testid="owner-bulk-role" onClick={() => setBulkRole({ role: 'moderator', scope_id: '' })}><SlidersHorizontal size={13} className="mr-1" />{t.bulk_change_role}</Button>
            <Button size="sm" variant="outline" className="h-7 text-[12px]" disabled={bulkBusy} data-testid="owner-bulk-suspend" onClick={() => runBulk('suspend')}><PauseCircle size={13} className="mr-1" />{t.suspend}</Button>
            <Button size="sm" variant="outline" className="h-7 text-[12px]" disabled={bulkBusy} data-testid="owner-bulk-reactivate" onClick={() => runBulk('reactivate')}><PlayCircle size={13} className="mr-1" />{t.reactivate}</Button>
            <Button size="sm" variant="outline" className="h-7 text-[12px] text-rose-600 hover:text-rose-700" disabled={bulkBusy} data-testid="owner-bulk-remove" onClick={() => runBulk('remove')}><Trash2 size={13} className="mr-1" />{t.remove}</Button>
            <button className="p-1 rounded-md text-teal-700 hover:bg-teal-100" title={t.clear_selection} data-testid="owner-bulk-clear" onClick={() => setSelected([])}><X size={14} /></button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] uppercase tracking-wide text-slate-400 border-y border-slate-100 bg-slate-50/60">
                <th className="pl-5 pr-1 py-2.5 w-10"><Checkbox aria-label={t.select_all} checked={allSel} onCheckedChange={toggleAll} data-testid="owner-member-select-all" /></th>
                <th className="px-3 py-2.5 font-semibold">{t.name}</th>
                <th className="px-3 py-2.5 font-semibold">{t.role}</th>
                <th className="px-3 py-2.5 font-semibold">{t.scope}</th>
                <th className="px-3 py-2.5 font-semibold">{t.status}</th>
                <th className="px-3 py-2.5 font-semibold">{t.when}</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((m) => (
                <tr key={m.id} data-testid={`owner-member-row-${m.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                  <td className="pl-5 pr-1 py-3"><Checkbox checked={selected.includes(m.id)} onCheckedChange={() => toggleSel(m.id)} data-testid={`owner-member-select-${m.id}`} /></td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-slate-800">{m.name || m.email || m.phone}</div>
                    <div className="text-[11.5px] text-slate-400">{m.email || m.phone}
                      {m.status === 'pending' && m.invite_delivery && <span> · {deliveryLabel[m.invite_delivery] || m.invite_delivery}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3"><RoleBadge role={m.role} t={t} /></td>
                  <td className="px-3 py-3 text-slate-600">{m.scope_type === 'org' ? t.whole_org : (m.scope_name || '—')}</td>
                  <td className="px-3 py-3"><StatusBadge value={m.status} t={t} /></td>
                  <td className="px-3 py-3 text-slate-500">{fmtDate(m.created_at, lang)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 justify-end">
                      {m.status === 'pending' && (<>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed" title={t.resend}
                                disabled={busyId === m.id} data-testid={`owner-resend-invite-${m.id}`} onClick={() => resend(m)}><RefreshCw size={14} className={busyId === m.id ? 'animate-spin' : ''} /></button>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40" title={t.cancel_invite}
                                disabled={busyId === m.id} data-testid={`owner-cancel-invite-${m.id}`} onClick={() => cancelInvite(m)}><X size={14} /></button>
                      </>)}
                      {m.status === 'accepted' && (<>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-40" title={t.manage_member}
                                disabled={busyId === m.id} data-testid={`owner-manage-member-${m.id}`} onClick={() => openManage(m)}><SlidersHorizontal size={14} /></button>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed" title={t.suspend}
                                disabled={busyId === m.id} data-testid={`owner-suspend-member-${m.id}`} onClick={() => suspend(m)}><PauseCircle size={14} className={busyId === m.id ? 'animate-spin' : ''} /></button>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40" title={t.remove}
                                disabled={busyId === m.id} data-testid={`owner-remove-member-${m.id}`} onClick={() => removeMember(m)}><Trash2 size={14} /></button>
                      </>)}
                      {m.status === 'suspended' && (<>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed" title={t.reactivate}
                                disabled={busyId === m.id} data-testid={`owner-reactivate-member-${m.id}`} onClick={() => reactivate(m)}><PlayCircle size={14} className={busyId === m.id ? 'animate-spin' : ''} /></button>
                        <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40" title={t.remove}
                                disabled={busyId === m.id} data-testid={`owner-remove-member-${m.id}`} onClick={() => removeMember(m)}><Trash2 size={14} /></button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
              {!data && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">{t.loading}</td></tr>
              )}
              {data && (data.items || []).length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">{t.no_data}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <UpgradeDialog open={upgrade} onOpenChange={setUpgrade} t={t} body={t.cap_seats}
        onUpgrade={() => { setUpgrade(false); setNav('billing'); }} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md" data-testid="owner-invite-dialog">
          <DialogHeader>
            <DialogTitle>{t.invite_member}</DialogTitle>
            <DialogDescription className="sr-only">{t.invite_member}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px]">{t.role}</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="owner-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t.role_admin}</SelectItem>
                  <SelectItem value="moderator">{t.role_moderator}</SelectItem>
                  <SelectItem value="revisor">{t.role_revisor}</SelectItem>
                </SelectContent>
              </Select></div>
            {form.role !== 'revisor' && (
              <div><Label className="text-[12px]">{t.scope}</Label>
                <Select value={form.scope_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, scope_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="owner-invite-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.type === 'DIVISION' ? t.division : t.event})</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <div><Label className="text-[12px]">{t.email}</Label>
              <Input data-testid="owner-invite-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div><Label className="text-[12px]">{t.phone}</Label>
              <Input data-testid="owner-invite-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            <div><Label className="text-[12px]">{t.display_name}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t.cancel}</Button>
            <Button data-testid="owner-invite-send" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={invite}>
              <Send size={14} className="mr-1.5" />{t.send_invite}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manageDlg} onOpenChange={(o) => !o && setManageDlg(null)}>
        <DialogContent className="max-w-md" data-testid="owner-manage-member-dialog">
          <DialogHeader>
            <DialogTitle>{t.manage_member}</DialogTitle>
            <DialogDescription className="sr-only">{t.manage_member}</DialogDescription>
          </DialogHeader>
          {manageDlg && (
            <div className="space-y-3">
              <div><Label className="text-[12px]">{t.role}</Label>
                <Select value={manageDlg.role} onValueChange={(v) => setManageDlg((d) => ({ ...d, role: v }))}>
                  <SelectTrigger data-testid="owner-manage-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t.role_admin}</SelectItem>
                    <SelectItem value="moderator">{t.role_moderator}</SelectItem>
                    <SelectItem value="revisor">{t.role_revisor}</SelectItem>
                  </SelectContent>
                </Select></div>
              {manageDlg.role !== 'revisor' && (
                <div><Label className="text-[12px]">{t.scope}</Label>
                  <Select value={manageDlg.scope_id || 'none'} onValueChange={(v) => setManageDlg((d) => ({ ...d, scope_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="owner-manage-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {manageDlg.scope_id && !subs.some((s) => s.id === manageDlg.scope_id) && (
                        <SelectItem value={manageDlg.scope_id}>{(manageDlg.scope_name || '—')} ({t.st_deleted})</SelectItem>
                      )}
                      {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.type === 'DIVISION' ? t.division : t.event})</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageDlg(null)}>{t.cancel}</Button>
            <Button data-testid="owner-manage-save" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={savingManage} onClick={saveManage}>{t.save_changes}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bulkRole} onOpenChange={(o) => !o && setBulkRole(null)}>
        <DialogContent className="max-w-md" data-testid="owner-bulk-role-dialog">
          <DialogHeader>
            <DialogTitle>{t.bulk_change_role}</DialogTitle>
            <DialogDescription className="sr-only">{t.bulk_change_role}</DialogDescription>
          </DialogHeader>
          {bulkRole && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-slate-500">{selected.length} {t.selected_count}</p>
              <div><Label className="text-[12px]">{t.role}</Label>
                <Select value={bulkRole.role} onValueChange={(v) => setBulkRole((d) => ({ ...d, role: v }))}>
                  <SelectTrigger data-testid="owner-bulk-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t.role_admin}</SelectItem>
                    <SelectItem value="moderator">{t.role_moderator}</SelectItem>
                    <SelectItem value="revisor">{t.role_revisor}</SelectItem>
                  </SelectContent>
                </Select></div>
              {bulkRole.role !== 'revisor' && (
                <div><Label className="text-[12px]">{t.scope}</Label>
                  <Select value={bulkRole.scope_id || 'keep'} onValueChange={(v) => setBulkRole((d) => ({ ...d, scope_id: v === 'keep' ? '' : v }))}>
                    <SelectTrigger data-testid="owner-bulk-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">{t.keep_scope}</SelectItem>
                      {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.type === 'DIVISION' ? t.division : t.event})</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRole(null)}>{t.cancel}</Button>
            <Button data-testid="owner-bulk-role-apply" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={bulkBusy}
                    onClick={() => runBulk('role', bulkRole.role === 'revisor' ? { role: 'revisor' } : { role: bulkRole.role, ...(bulkRole.scope_id ? { scope_id: bulkRole.scope_id } : {}) })}>{t.apply}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== Swish pool ============================== */

export function SwishSection({ orgId, headers, t, setNav }) {
  const [accounts, setAccounts] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [usage, setUsage] = useState(null);
  const [form, setForm] = useState({ merchant_number: '', display_name: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/swish-accounts`, { headers });
      setAccounts(res.data.items || []);
    } catch (e) { console.error(e); toast.error(errMsg(e)); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/org/${orgId}/usage`, { headers }).then((u) => setUsage(u.data)).catch(() => {});
  }, [orgId, headers]);

  const add = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/business/swish-accounts`, form, { headers });
      toast.success(t.saved); setDialog(false);
      setForm({ merchant_number: '', display_name: '', is_primary: false });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const setPrimary = async (a) => {
    try { await axios.put(`${API}/business/swish-accounts/${a.id}/set-primary`, {}, { headers }); toast.success(t.saved); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm(t.confirm_del_swish)) return;
    try { await axios.delete(`${API}/business/swish-accounts/${a.id}`, { headers }); toast.success(t.removed); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <Panel testId="owner-swish-pool">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <SectionTitle sub={t.swish_sub}>{t.swish_title}</SectionTitle>
          <Button size="sm" data-testid="owner-add-swish" className="bg-teal-700 hover:bg-teal-800 text-white h-8 text-[13px]"
                  onClick={() => (atCap(usage?.swish_accounts) ? setUpgrade(true) : setDialog(true))}>
            + {t.add_account}
          </Button>
        </div>
        <div className="p-5 pt-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(accounts || []).map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`owner-swish-${a.id}`}>
              <span className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 font-bold text-[12px]">SW</span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold truncate flex items-center gap-1.5">
                  {a.display_name}
                  {a.is_primary && <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 text-[10px] px-1.5 py-0">{t.primary}</Badge>}
                </div>
                <div className="text-[11.5px] text-slate-400 tabular-nums">{a.merchant_number}</div>
              </div>
              <div className="flex gap-1">
                {!a.is_primary && (
                  <button className="p-1.5 rounded-md text-slate-400 hover:text-amber-500 hover:bg-amber-50" title={t.set_primary} onClick={() => setPrimary(a)}>
                    <Star size={14} /></button>
                )}
                <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50" title={t.delete} onClick={() => remove(a)}>
                  <Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {accounts && accounts.length === 0 && <p className="text-[13px] text-slate-400 col-span-full py-6 text-center">{t.no_data}</p>}
        </div>
      </Panel>

      <UpgradeDialog open={upgrade} onOpenChange={setUpgrade} t={t} body={t.cap_swish}
        onUpgrade={() => { setUpgrade(false); setNav('billing'); }} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-sm" data-testid="owner-swish-dialog">
          <DialogHeader>
            <DialogTitle>{t.add_account}</DialogTitle>
            <DialogDescription className="sr-only">{t.add_account}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px]">{t.merchant_number}</Label>
              <Input data-testid="owner-swish-number" placeholder="123XXXXXXX" value={form.merchant_number}
                     onChange={(e) => setForm((f) => ({ ...f, merchant_number: e.target.value }))} /></div>
            <div><Label className="text-[12px]">{t.display_name}</Label>
              <Input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t.cancel}</Button>
            <Button data-testid="owner-swish-save" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={add}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== Manage team (per division) ============================== */

function ManageTeamDialog({ orgId, headers, t, division, onClose }) {
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/org/${orgId}/members`, { headers });
      setMembers((res.data.items || []).filter((m) => m.scope_id === division.id));
    } catch (e) { toast.error(errMsg(e)); }
  }, [orgId, headers, division.id]);
  useEffect(() => { load(); }, [load]);

  const addModerator = async () => {
    if (!email.trim()) { toast.error(t.email_or_phone_required); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/org/${orgId}/members`,
        { role: 'moderator', scope_id: division.id, email: email.trim() }, { headers });
      toast.success(t.invited); setEmail(''); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const removeM = async (m) => {
    try {
      if (m.status === 'pending') await axios.post(`${API}/org/${orgId}/members/${m.id}/cancel`, {}, { headers });
      else await axios.delete(`${API}/org/${orgId}/members/${m.id}`, { headers });
      toast.success(t.removed); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="owner-team-dialog">
        <DialogHeader>
          <DialogTitle>{t.manage_team} · {division.name}</DialogTitle>
          <DialogDescription className="sr-only">{t.team_of} {division.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          {!members && <p className="text-[13px] text-slate-400 py-4 text-center">{t.loading}</p>}
          {members && members.length === 0 && <p className="text-[13px] text-slate-400 py-4 text-center">{t.no_team}</p>}
          {(members || []).map((m) => (
            <div key={m.id} className="flex items-center gap-2 border border-slate-100 rounded-lg px-3 py-2" data-testid={`owner-team-member-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{m.name || m.email || m.phone}</div>
                <div className="text-[11.5px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                  <RoleBadge role={m.role} t={t} /><StatusBadge value={m.status} t={t} />
                </div>
              </div>
              <button className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50" title={t.remove}
                      data-testid={`owner-team-remove-${m.id}`} onClick={() => removeM(m)}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <Label className="text-[12px]">{t.add_moderator}</Label>
          <div className="flex gap-2">
            <Input data-testid="owner-team-email" type="email" placeholder={t.email} value={email}
                   onChange={(e) => setEmail(e.target.value)} />
            <Button data-testid="owner-team-add" className="bg-teal-700 hover:bg-teal-800 text-white shrink-0" disabled={saving} onClick={addModerator}>
              <UserPlus size={14} className="mr-1.5" />{t.add_moderator}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.close}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
